import instance from "@/http";
import moment from "moment";
import { exportCsv, parseCsv } from "@/utils/csv";
import {
  FeeCalculationStatus,
  PaymentBreakdown,
  PaymentComponent,
  resolvePaymentAlias,
  resolvePaymentBreakdown,
} from "@/utils/paymentBreakdown";
import {
  ORDER_PATH_DEFINITIONS,
  OWNED_MALL_ORDER_PATH_GROUP,
  OrderPathPresentationMode,
  resolveOrderPath,
} from "@/utils/orderPathFilter";

export interface OrderPathItem {
  code: string;
  name: string;
  isSystem?: boolean;
}

export interface PaymentMethodItem {
  code: string;
  name: string;
  defaultRate: number;
  isSystem?: boolean;
}

export interface ComboFeeRule {
  pathCodeOrName: string;
  methodCodeOrName: string;
  rate: number;
}

export interface DynamicPgFeeSettings {
  vatIncluded: boolean;
  globalDefaultRate: number;
  orderPaths: OrderPathItem[];
  paymentMethods: PaymentMethodItem[];
  rules: ComboFeeRule[];

  // Legacy compatibility fields
  card?: number;
  bank?: number;
  vbank?: number;
  naverpay?: number;
  kakaopay?: number;
  etc?: number;
}

export interface FeeResolutionResult {
  rate: number;
  effectiveRate: number;
  isUnconfigured: boolean;
  pathCode: string;
  pathName: string;
  methodCode: string;
  methodName: string;
  sourceDescription: string;
}

export interface UnconfiguredComboItem {
  pathCode: string;
  pathName: string;
  methodCode: string;
  methodName: string;
  count: number;
  samplePrice: number;
}

export interface PgFeeCalculation {
  fee: number;
  status: FeeCalculationStatus;
  breakdown: PaymentBreakdown;
  componentFees: Array<
    PaymentComponent & { fee: number; effectiveRate: number }
  >;
  reason?: string;
}

export interface PgFeeSummary {
  fee: number;
  exactCount: number;
  estimatedCount: number;
  unavailableCount: number;
  orderCount: number;
}

export const DEFAULT_ORDER_PATHS: OrderPathItem[] = [
  { code: "cafe24", name: "PC쇼핑몰", isSystem: true },
  { code: "mobile", name: "모바일웹", isSystem: true },
  { code: "mobile_d", name: "모바일앱", isSystem: true },
  { code: "NCHECKOUT", name: "네이버 페이", isSystem: true },
  { code: "shopn", name: "스마트스토어", isSystem: true },
  { code: "gmarket", name: "G마켓", isSystem: true },
  { code: "auction", name: "옥션", isSystem: true },
  { code: "sk11st", name: "11번가", isSystem: true },
  { code: "coupang", name: "쿠팡", isSystem: true },
  { code: "inpark", name: "인터파크", isSystem: true },
  { code: "kakaostore", name: "카카오톡 스토어", isSystem: true },
  { code: "10x10", name: "텐바이텐", isSystem: true },
  { code: "hottracks", name: "핫트랙스", isSystem: true },
];

export const DEFAULT_PAYMENT_METHODS: PaymentMethodItem[] = [
  { code: "card", name: "신용카드", defaultRate: 2.2, isSystem: true },
  { code: "cash", name: "무통장입금", defaultRate: 0.0, isSystem: true },
  { code: "tcash", name: "계좌이체", defaultRate: 1.8, isSystem: true },
  { code: "icash", name: "가상계좌", defaultRate: 1.5, isSystem: true },
  { code: "cell", name: "휴대폰", defaultRate: 2.5, isSystem: true },
  { code: "point", name: "선불금", defaultRate: 0.0, isSystem: true },
  { code: "mileage", name: "적립금", defaultRate: 0.0, isSystem: true },
  { code: "deposit", name: "예치금", defaultRate: 0.0, isSystem: true },
  { code: "deferpay", name: "후불", defaultRate: 3.3, isSystem: true },
  { code: "cvs", name: "편의점", defaultRate: 2.5, isSystem: true },
  { code: "giftcard", name: "제휴상품권", defaultRate: 3.0, isSystem: true },
  { code: "pointcard", name: "제휴포인트", defaultRate: 3.0, isSystem: true },
  { code: "etc", name: "기타", defaultRate: 2.5, isSystem: true },
];

export const DEFAULT_DYNAMIC_PG_FEE_SETTINGS: DynamicPgFeeSettings = {
  vatIncluded: true,
  globalDefaultRate: 2.2,
  orderPaths: DEFAULT_ORDER_PATHS,
  paymentMethods: DEFAULT_PAYMENT_METHODS,
  rules: [
    { pathCodeOrName: "cafe24", methodCodeOrName: "card", rate: 2.2 },
    { pathCodeOrName: "cafe24", methodCodeOrName: "cash", rate: 0.0 },
    { pathCodeOrName: "mobile", methodCodeOrName: "card", rate: 2.2 },
    { pathCodeOrName: "mobile", methodCodeOrName: "cash", rate: 0.0 },
    { pathCodeOrName: "NCHECKOUT", methodCodeOrName: "card", rate: 3.3 },
    { pathCodeOrName: "NCHECKOUT", methodCodeOrName: "point", rate: 3.74 },
    { pathCodeOrName: "shopn", methodCodeOrName: "card", rate: 3.3 },
    { pathCodeOrName: "shopn", methodCodeOrName: "tcash", rate: 1.8 },
    { pathCodeOrName: "shopn", methodCodeOrName: "cash", rate: 0.0 },
    { pathCodeOrName: "shopn", methodCodeOrName: "mileage", rate: 0.0 },
    { pathCodeOrName: "shopn", methodCodeOrName: "deferpay", rate: 3.3 },
    { pathCodeOrName: "shopn", methodCodeOrName: "cell", rate: 3.74 },
  ],
};

const PG_FEE_STORAGE_KEY = "jungslab_pg_fee_rates_v2";
let cachedSettings: DynamicPgFeeSettings | null = null;

export function migratePgFeeSettings(parsedData: any): DynamicPgFeeSettings {
  if (!parsedData || typeof parsedData !== "object") {
    return { ...DEFAULT_DYNAMIC_PG_FEE_SETTINGS };
  }

  const vatIncluded =
    typeof parsedData.vatIncluded === "boolean" ? parsedData.vatIncluded : true;
  const globalDefaultRate =
    typeof parsedData.globalDefaultRate === "number"
      ? parsedData.globalDefaultRate
      : 2.2;

  // Merge order paths
  const orderPaths: OrderPathItem[] = Array.isArray(parsedData.orderPaths)
    ? [...parsedData.orderPaths]
    : [];

  DEFAULT_ORDER_PATHS.forEach((defPath) => {
    const exists = orderPaths.some(
      (p) =>
        p.code.toLowerCase() === defPath.code.toLowerCase() ||
        p.name.toLowerCase() === defPath.name.toLowerCase()
    );
    if (!exists) {
      orderPaths.push({ ...defPath });
    }
  });

  // Merge payment methods
  const paymentMethods: PaymentMethodItem[] = Array.isArray(
    parsedData.paymentMethods
  )
    ? [...parsedData.paymentMethods]
    : [];

  DEFAULT_PAYMENT_METHODS.forEach((defMethod) => {
    const exists = paymentMethods.some(
      (m) =>
        m.code.toLowerCase() === defMethod.code.toLowerCase() ||
        m.name.toLowerCase() === defMethod.name.toLowerCase()
    );
    if (!exists) {
      paymentMethods.push({ ...defMethod });
    }
  });

  let rules: ComboFeeRule[] = Array.isArray(parsedData.rules)
    ? [...parsedData.rules]
    : [];

  // Migration from older pathRates if present
  if (rules.length === 0 && Array.isArray(parsedData.pathRates)) {
    parsedData.pathRates.forEach((pr: any) => {
      if (pr && pr.rates && typeof pr.rates === "object") {
        Object.keys(pr.rates).forEach((mKey) => {
          const val = pr.rates[mKey];
          if (typeof val === "number") {
            rules.push({
              pathCodeOrName: pr.id || pr.name,
              methodCodeOrName: mKey,
              rate: val,
            });
          }
        });
      }
    });
  }

  if (rules.length === 0) {
    rules = [...DEFAULT_DYNAMIC_PG_FEE_SETTINGS.rules];
  }

  return {
    vatIncluded,
    globalDefaultRate,
    orderPaths,
    paymentMethods,
    rules,
  };
}

export function loadPgFeeRates(): DynamicPgFeeSettings {
  if (cachedSettings) {
    return cachedSettings;
  }
  try {
    const raw = localStorage.getItem(PG_FEE_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const migrated = migratePgFeeSettings(parsed);
      cachedSettings = migrated;
      return migrated;
    }
  } catch (e) {
    console.error("Failed to load PG fee settings", e);
  }
  const defaultRes = migratePgFeeSettings(DEFAULT_DYNAMIC_PG_FEE_SETTINGS);
  cachedSettings = defaultRes;
  return defaultRes;
}

export function savePgFeeRates(settings: DynamicPgFeeSettings): void {
  try {
    const migrated = migratePgFeeSettings(settings);
    cachedSettings = migrated;
    localStorage.setItem(PG_FEE_STORAGE_KEY, JSON.stringify(migrated));
  } catch (e) {
    console.error("Failed to save PG fee settings", e);
  }
}

export async function fetchPgFeeRatesFromServer(): Promise<DynamicPgFeeSettings | null> {
  try {
    const res = await instance.get("/settings/pg-fee");
    if (res.data) {
      const settings = migratePgFeeSettings(res.data);
      savePgFeeRates(settings);
      return settings;
    }
  } catch (e) {
    console.error("Failed to fetch PG fee settings from server", e);
    throw e;
  }
  return null;
}

export async function savePgFeeRatesToServer(
  settings: DynamicPgFeeSettings
): Promise<boolean> {
  const migrated = migratePgFeeSettings(settings);
  try {
    await instance.post("/settings/pg-fee", {
      ...migrated,
      // Pass top-level fallbacks for legacy server validation
      card:
        migrated.paymentMethods.find((m) => m.code === "card")?.defaultRate ??
        2.2,
      bank:
        migrated.paymentMethods.find((m) => m.code === "tcash")?.defaultRate ??
        1.8,
      vbank:
        migrated.paymentMethods.find((m) => m.code === "icash")?.defaultRate ??
        1.5,
      naverpay: 3.3,
      kakaopay: 3.3,
      etc: 2.5,
    });
    savePgFeeRates(migrated);
    return true;
  } catch (e) {
    console.error("Failed to save PG fee settings to server", e);
    return false;
  }
}

/**
 * Finds matching OrderPathItem for a given raw string or object.
 */
export function findOrderPath(
  rawPath: any,
  settings: DynamicPgFeeSettings = loadPgFeeRates(),
  presentationMode: OrderPathPresentationMode = "grouped"
): OrderPathItem {
  if (rawPath && typeof rawPath === "object") {
    const resolved = resolveOrderPath(rawPath, presentationMode);
    if (resolved.sourceId) {
      return (
        (settings.orderPaths || DEFAULT_ORDER_PATHS).find(
          (path) =>
            path.code.toLowerCase() === resolved.feePathCode.toLowerCase()
        ) || { code: resolved.feePathCode, name: resolved.label }
      );
    }
  }
  const str = (
    typeof rawPath === "string"
      ? rawPath
      : typeof rawPath === "object" && rawPath !== null
      ? (rawPath as any).order_place_name ||
        (rawPath as any).order_place ||
        (rawPath as any).name ||
        (rawPath as any).code ||
        String(rawPath)
      : String(rawPath || "")
  ).trim();

  const lower = str.toLowerCase();
  const list = settings.orderPaths || DEFAULT_ORDER_PATHS;

  if (!lower) return list[0];

  // 1. Exact code or name match
  const exact = list.find(
    (p) => p.code.toLowerCase() === lower || p.name.toLowerCase() === lower
  );
  if (exact) return exact;

  // 2. Sub-string match
  const contains = list.find(
    (p) =>
      lower.includes(p.name.toLowerCase()) ||
      p.name.toLowerCase().includes(lower) ||
      lower.includes(p.code.toLowerCase())
  );
  if (contains) return contains;

  // 3. Keyword fallbacks
  if (lower.includes("스마트스토어") || lower.includes("shopn")) {
    return list.find((p) => p.code === "shopn") || { code: "shopn", name: str };
  }
  if (
    lower.includes("네이버") ||
    lower.includes("naver") ||
    lower.includes("ncheckout")
  ) {
    return (
      list.find((p) => p.code === "NCHECKOUT") || {
        code: "NCHECKOUT",
        name: str,
      }
    );
  }
  if (
    lower.includes("pc") ||
    lower.includes("자사") ||
    lower.includes("cafe24")
  ) {
    return (
      list.find((p) => p.code === "cafe24") || { code: "cafe24", name: str }
    );
  }

  // Create temporary item if completely new path
  return { code: str, name: str };
}

/**
 * Finds matching PaymentMethodItem for a given raw string or object.
 */
export function findPaymentMethod(
  rawMethod: any,
  settings: DynamicPgFeeSettings = loadPgFeeRates()
): PaymentMethodItem {
  const str = (
    typeof rawMethod === "string"
      ? rawMethod
      : typeof rawMethod === "object" && rawMethod !== null
      ? (rawMethod as any).payment_method_name ||
        (rawMethod as any).name ||
        (rawMethod as any).code ||
        String(rawMethod)
      : String(rawMethod || "")
  ).trim();

  const lower = str.toLowerCase();
  const list = settings.paymentMethods || DEFAULT_PAYMENT_METHODS;

  if (!lower) return list[0];

  const alias = resolvePaymentAlias(str);
  if (alias) {
    const configured = list.find(
      (m) =>
        m.code.toLowerCase() === alias.code.toLowerCase() ||
        m.name.toLowerCase() === alias.name.toLowerCase()
    );
    if (configured) return configured;
  }

  // 1. Exact code or name match
  const exact = list.find(
    (m) => m.code.toLowerCase() === lower || m.name.toLowerCase() === lower
  );
  if (exact) return exact;

  // 2. Sub-string match
  const contains = list.find(
    (m) =>
      lower.includes(m.name.toLowerCase()) ||
      m.name.toLowerCase().includes(lower) ||
      lower.includes(m.code.toLowerCase())
  );
  if (contains) return contains;

  // 3. Keyword fallbacks
  if (lower.includes("카드") || lower.includes("card"))
    return list.find((m) => m.code === "card") || list[0];
  if (lower.includes("무통장") || lower.includes("cash"))
    return list.find((m) => m.code === "cash") || list[1];
  if (lower.includes("가상") || lower.includes("icash"))
    return list.find((m) => m.code === "icash") || list[3];
  if (lower.includes("계좌") || lower.includes("tcash"))
    return list.find((m) => m.code === "tcash") || list[2];
  if (lower.includes("휴대폰") || lower.includes("cell"))
    return list.find((m) => m.code === "cell") || list[4];
  if (lower.includes("적립금") || lower.includes("mileage"))
    return list.find((m) => m.code === "mileage") || list[6];
  if (lower.includes("선불") || lower.includes("point"))
    return list.find((m) => m.code === "point") || list[5];

  return { code: str, name: str, defaultRate: 2.2 };
}

/**
 * Resolves fee rate with unconfigured rate detection & fallback chain.
 */
export function resolveFeeRate(
  rawPath: any,
  rawMethod: any,
  settings: DynamicPgFeeSettings = loadPgFeeRates(),
  presentationMode: OrderPathPresentationMode = "grouped"
): FeeResolutionResult {
  const resolvedPath =
    rawPath && typeof rawPath === "object"
      ? resolveOrderPath(rawPath, presentationMode)
      : null;
  const pathItem = findOrderPath(rawPath, settings, presentationMode);
  const methodItem = findPaymentMethod(rawMethod, settings);

  const rules = settings.rules || [];
  const matchedRule = rules.find(
    (r) =>
      (r.pathCodeOrName.toLowerCase() === pathItem.code.toLowerCase() ||
        r.pathCodeOrName.toLowerCase() === pathItem.name.toLowerCase()) &&
      (r.methodCodeOrName.toLowerCase() === methodItem.code.toLowerCase() ||
        r.methodCodeOrName.toLowerCase() === methodItem.name.toLowerCase())
  );

  let baseRate = 0;
  let isUnconfigured = false;
  let sourceDesc = "";

  if (
    matchedRule &&
    typeof matchedRule.rate === "number" &&
    !isNaN(matchedRule.rate)
  ) {
    baseRate = matchedRule.rate;
    isUnconfigured = false;
    sourceDesc = `지정 조합 수수료율 (${
      resolvedPath?.label || pathItem.name
    } x ${methodItem.name})`;
  } else {
    isUnconfigured = true;
    baseRate =
      typeof methodItem.defaultRate === "number"
        ? methodItem.defaultRate
        : settings.globalDefaultRate ?? 2.2;
    sourceDesc = `결제수단 기본 수수료율 (${methodItem.name} 미설정 폴백)`;
  }

  let effectiveRate = baseRate;
  if (settings.vatIncluded) {
    effectiveRate = Math.round(baseRate * 1.1 * 100) / 100;
  }

  return {
    rate: baseRate,
    effectiveRate,
    isUnconfigured,
    pathCode: pathItem.code,
    pathName: resolvedPath?.label || pathItem.name,
    methodCode: methodItem.code,
    methodName: methodItem.name,
    sourceDescription: sourceDesc,
  };
}

/**
 * Returns estimated PG fee rate percentage.
 */
export function getPgFeeRate(
  rawMethod: any,
  settings: DynamicPgFeeSettings = loadPgFeeRates(),
  rawPath?: any,
  presentationMode: OrderPathPresentationMode = "grouped"
): number {
  let path = rawPath;
  if (!path && typeof rawMethod === "object" && rawMethod !== null) {
    path = rawMethod;
  }

  const res = resolveFeeRate(path, rawMethod, settings, presentationMode);
  return res.effectiveRate;
}

/**
 * Calculates estimated PG fee amount for a given payment method, amount, and order path.
 */
export function calculatePgFee(
  rawMethod: any,
  amount: number,
  settings: DynamicPgFeeSettings = loadPgFeeRates(),
  rawPath?: any,
  presentationMode: OrderPathPresentationMode = "grouped"
): number {
  if (!amount || amount <= 0) return 0;
  const rate = getPgFeeRate(rawMethod, settings, rawPath, presentationMode);
  return Math.round(amount * (rate / 100));
}

function calculateComponentFee(
  component: PaymentComponent,
  amount: number,
  settings: DynamicPgFeeSettings,
  rawPath?: any,
  presentationMode: OrderPathPresentationMode = "grouped"
): { fee: number; effectiveRate: number } {
  if (component.isDiscount || amount <= 0) {
    return { fee: 0, effectiveRate: 0 };
  }
  const resolution = resolveFeeRate(
    rawPath,
    component.code,
    settings,
    presentationMode
  );
  return {
    fee: Math.round(amount * (resolution.effectiveRate / 100)),
    effectiveRate: resolution.effectiveRate,
  };
}

/**
 * Calculates an order's PG fee using payment components when their amounts
 * are available. Composite payment names without component amounts remain
 * explicitly estimated instead of silently treating every component as the
 * first matching payment method.
 */
export function calculatePgFeeForItem(
  item: any,
  settings: DynamicPgFeeSettings = loadPgFeeRates(),
  rawPath?: any,
  presentationMode: OrderPathPresentationMode = "grouped"
): PgFeeCalculation {
  const breakdown = resolvePaymentBreakdown(item);
  const path = rawPath || item;

  if (breakdown.status === "unavailable") {
    return {
      fee: 0,
      status: "unavailable",
      breakdown,
      componentFees: [],
      reason: breakdown.reason,
    };
  }

  const componentFees: Array<
    PaymentComponent & {
      fee: number;
      effectiveRate: number;
    }
  > = [];

  if (breakdown.status === "exact") {
    breakdown.components.forEach((component) => {
      const amount = component.amount || 0;
      const result = calculateComponentFee(
        component,
        amount,
        settings,
        path,
        presentationMode
      );
      componentFees.push({
        ...component,
        fee: result.fee,
        effectiveRate: result.effectiveRate,
      });
    });
  } else {
    const fallback = breakdown.components.find(
      (component) => component.code === breakdown.fallbackCode
    );
    if (fallback) {
      const result = calculateComponentFee(
        fallback,
        breakdown.totalAmount,
        settings,
        path,
        presentationMode
      );
      componentFees.push({
        ...fallback,
        amount: breakdown.totalAmount,
        fee: result.fee,
        effectiveRate: result.effectiveRate,
      });
    }
  }

  return {
    fee: componentFees.reduce((sum, component) => sum + component.fee, 0),
    status: breakdown.status,
    breakdown,
    componentFees,
    reason: breakdown.reason,
  };
}

export function summarizePgFees(
  items: any[],
  settings: DynamicPgFeeSettings = loadPgFeeRates(),
  presentationMode: OrderPathPresentationMode = "grouped"
): PgFeeSummary {
  const summary: PgFeeSummary = {
    fee: 0,
    exactCount: 0,
    estimatedCount: 0,
    unavailableCount: 0,
    orderCount: 0,
  };

  if (!Array.isArray(items)) return summary;
  items.forEach((item) => {
    if (!item || Array.isArray(item.refund_methods)) return;
    const calculation = calculatePgFeeForItem(
      item,
      settings,
      undefined,
      presentationMode
    );
    summary.fee += calculation.fee;
    summary.orderCount += 1;
    if (calculation.status === "exact") summary.exactCount += 1;
    if (calculation.status === "estimated") summary.estimatedCount += 1;
    if (calculation.status === "unavailable") summary.unavailableCount += 1;
  });
  return summary;
}

/**
 * Scans an array of orders/refunds and detects unconfigured combinations.
 */
export function detectUnconfiguredCombinations(
  items: any[],
  settings: DynamicPgFeeSettings = loadPgFeeRates(),
  presentationMode: OrderPathPresentationMode = "grouped"
): UnconfiguredComboItem[] {
  if (!Array.isArray(items) || items.length === 0) return [];

  const map: Record<string, UnconfiguredComboItem> = {};

  items.forEach((item) => {
    if (!item) return;
    const rawPath = item;
    const rawMethod =
      item.payment_method_name ||
      item.order_payment_method ||
      item.order?.payment_method_name ||
      "신용카드";

    const breakdown = resolvePaymentBreakdown(item);
    const methods = breakdown.components.filter(
      (component) => !component.isDiscount
    );
    const methodList =
      methods.length > 0
        ? methods
        : rawMethod
        ? [
            {
              code: resolvePaymentAlias(rawMethod)?.code || String(rawMethod),
              name: String(rawMethod),
              isDiscount: false,
            },
          ]
        : [];

    methodList.forEach((component) => {
      const res = resolveFeeRate(
        rawPath,
        component.code,
        settings,
        presentationMode
      );
      if (!res.isUnconfigured) return;

      const key = `${res.pathCode}:${res.methodCode}`;
      const price =
        breakdown.totalAmount ||
        parseInt(item?.initial_order_amount?.order_price_amount, 10) ||
        parseInt(item?.payment_composition_amount, 10) ||
        0;

      if (!map[key]) {
        map[key] = {
          pathCode: res.pathCode,
          pathName: res.pathName,
          methodCode: res.methodCode,
          methodName: res.methodName,
          count: 0,
          samplePrice: price,
        };
      }
      map[key].count += 1;
    });
  });

  return Object.values(map);
}

/**
 * Exports current PG fee rates settings as a CSV file.
 */
export function exportPgFeeCsv(
  settings: DynamicPgFeeSettings = loadPgFeeRates(),
  presentationMode: OrderPathPresentationMode = "grouped"
): void {
  const headers = [
    "주문경로 코드",
    "주문경로명",
    ...settings.paymentMethods.map((m) => `${m.name} (${m.code})`),
  ];

  const groupedFeeCodes = new Set(
    ORDER_PATH_DEFINITIONS.filter((definition) =>
      OWNED_MALL_ORDER_PATH_GROUP.memberIds.includes(definition.id)
    ).map((definition) => definition.feePathCode.toLowerCase())
  );
  const feeSourceDefinition = ORDER_PATH_DEFINITIONS.find(
    (definition) => definition.id === OWNED_MALL_ORDER_PATH_GROUP.feeSourceId
  );
  const exportPaths =
    presentationMode === "grouped"
      ? [
          {
            code: feeSourceDefinition?.feePathCode || "cafe24",
            name: OWNED_MALL_ORDER_PATH_GROUP.label,
            isSystem: true,
          },
          ...settings.orderPaths.filter(
            (path) => !groupedFeeCodes.has(path.code.toLowerCase())
          ),
        ]
      : settings.orderPaths;

  const rows: Array<Array<string | number>> = exportPaths.map((path) => {
    const row: Array<string | number> = [path.code, path.name];
    settings.paymentMethods.forEach((method) => {
      const rule = settings.rules.find(
        (r) =>
          (r.pathCodeOrName.toLowerCase() === path.code.toLowerCase() ||
            r.pathCodeOrName.toLowerCase() === path.name.toLowerCase()) &&
          (r.methodCodeOrName.toLowerCase() === method.code.toLowerCase() ||
            r.methodCodeOrName.toLowerCase() === method.name.toLowerCase())
      );
      if (rule && typeof rule.rate === "number" && !isNaN(rule.rate)) {
        row.push(rule.rate);
      } else {
        row.push(method.defaultRate ?? settings.globalDefaultRate ?? 2.2);
      }
    });
    return row;
  });

  const filename = `PG_수수료율_설정_${moment().format("YYYYMMDD_HHmmss")}.csv`;
  exportCsv(headers, rows, filename);
}

export interface ImportCsvResult {
  settings: DynamicPgFeeSettings;
  updatedCount: number;
  addedPaths: number;
  addedMethods: number;
}

/**
 * Parses and imports PG fee settings from CSV text.
 */
export function importPgFeeCsv(
  csvText: string,
  currentSettings: DynamicPgFeeSettings = loadPgFeeRates()
): ImportCsvResult {
  const rows = parseCsv(csvText);
  if (!rows || rows.length < 2) {
    throw new Error("CSV 파일에 유효한 데이터 행이 없습니다.");
  }

  const newSettings: DynamicPgFeeSettings = JSON.parse(
    JSON.stringify(currentSettings)
  );

  if (!Array.isArray(newSettings.orderPaths)) newSettings.orderPaths = [];
  if (!Array.isArray(newSettings.paymentMethods))
    newSettings.paymentMethods = [];
  if (!Array.isArray(newSettings.rules)) newSettings.rules = [];

  let updatedCount = 0;
  let addedPaths = 0;
  let addedMethods = 0;

  const header = rows[0];

  const isListFormat = header.some(
    (h) =>
      h.includes("결제수단") ||
      h.includes("payment_method") ||
      (h.includes("수단") && !h.includes("경로"))
  );

  if (isListFormat) {
    let pathCol = header.findIndex(
      (h) => h.includes("주문경로") || h.includes("경로") || h.includes("path")
    );
    if (pathCol === -1) pathCol = 0;

    let methodCol = header.findIndex(
      (h) =>
        h.includes("결제수단") || h.includes("수단") || h.includes("method")
    );
    if (methodCol === -1) methodCol = 1;

    let rateCol = header.findIndex(
      (h) =>
        h.includes("수수료") ||
        h.includes("율") ||
        h.includes("rate") ||
        h.includes("%")
    );
    if (rateCol === -1) rateCol = 2;

    for (let r = 1; r < rows.length; r++) {
      const row = rows[r];
      if (!row || row.length <= Math.max(pathCol, methodCol, rateCol)) continue;

      const rawPathStr = row[pathCol];
      const rawMethodStr = row[methodCol];
      const rawRateStr = row[rateCol];

      if (!rawPathStr || !rawMethodStr) continue;

      const rateVal = parseFloat(
        rawRateStr.replace("%", "").replace(",", ".").trim()
      );
      if (isNaN(rateVal)) continue;

      let pathObj = findOrderPath(rawPathStr, newSettings);
      const existingPath = newSettings.orderPaths.find(
        (p) =>
          p.code.toLowerCase() === pathObj.code.toLowerCase() ||
          p.name.toLowerCase() === pathObj.name.toLowerCase()
      );
      if (!existingPath) {
        newSettings.orderPaths.push(pathObj);
        addedPaths++;
      } else {
        pathObj = existingPath;
      }

      let methodObj = findPaymentMethod(rawMethodStr, newSettings);
      const existingMethod = newSettings.paymentMethods.find(
        (m) =>
          m.code.toLowerCase() === methodObj.code.toLowerCase() ||
          m.name.toLowerCase() === methodObj.name.toLowerCase()
      );
      if (!existingMethod) {
        newSettings.paymentMethods.push(methodObj);
        addedMethods++;
      } else {
        methodObj = existingMethod;
      }

      const ruleIdx = newSettings.rules.findIndex(
        (rule) =>
          (rule.pathCodeOrName.toLowerCase() === pathObj.code.toLowerCase() ||
            rule.pathCodeOrName.toLowerCase() === pathObj.name.toLowerCase()) &&
          (rule.methodCodeOrName.toLowerCase() ===
            methodObj.code.toLowerCase() ||
            rule.methodCodeOrName.toLowerCase() ===
              methodObj.name.toLowerCase())
      );

      if (ruleIdx !== -1) {
        newSettings.rules[ruleIdx].rate = rateVal;
      } else {
        newSettings.rules.push({
          pathCodeOrName: pathObj.code,
          methodCodeOrName: methodObj.code,
          rate: rateVal,
        });
      }
      updatedCount++;
    }
  } else {
    // Matrix format
    let pathCodeCol = 0;
    let pathNameCol = -1;
    let methodStartCol = 1;

    const h0 = (header[0] || "").toLowerCase();
    const h1 = (header[1] || "").toLowerCase();

    if (h0.includes("코드") || h0.includes("code")) {
      pathCodeCol = 0;
      if (
        h1.includes("경로") ||
        h1.includes("명") ||
        h1.includes("name") ||
        h1.includes("path")
      ) {
        pathNameCol = 1;
        methodStartCol = 2;
      }
    } else if (
      h0.includes("경로") ||
      h0.includes("path") ||
      h0.includes("쇼핑몰") ||
      h0.includes("몰")
    ) {
      pathCodeCol = 0;
      if (h1.includes("명") || h1.includes("name")) {
        pathNameCol = 1;
        methodStartCol = 2;
      }
    }

    const colMethods: PaymentMethodItem[] = [];
    for (let c = methodStartCol; c < header.length; c++) {
      const cellHeader = header[c];
      if (!cellHeader) continue;

      const matchParen = cellHeader.match(/\(([^)]+)\)/);
      const codeOrName = matchParen ? matchParen[1].trim() : cellHeader.trim();

      let methodObj = findPaymentMethod(codeOrName, newSettings);
      const existingMethod = newSettings.paymentMethods.find(
        (m) =>
          m.code.toLowerCase() === methodObj.code.toLowerCase() ||
          m.name.toLowerCase() === methodObj.name.toLowerCase() ||
          m.name.toLowerCase() === cellHeader.trim().toLowerCase()
      );

      if (!existingMethod) {
        const cleanName =
          cellHeader.replace(/\([^)]+\)/, "").trim() || codeOrName;
        methodObj = {
          code: codeOrName,
          name: cleanName,
          defaultRate: 2.2,
          isSystem: false,
        };
        newSettings.paymentMethods.push(methodObj);
        addedMethods++;
      } else {
        methodObj = existingMethod;
      }
      colMethods[c] = methodObj;
    }

    for (let r = 1; r < rows.length; r++) {
      const row = rows[r];
      if (!row || row.length === 0) continue;

      const rawPathCode = row[pathCodeCol] || "";
      const rawPathName = pathNameCol !== -1 ? row[pathNameCol] || "" : "";
      const rawPath = rawPathName || rawPathCode;

      if (!rawPath) continue;

      let pathObj = findOrderPath(rawPathCode || rawPathName, newSettings);
      const existingPath = newSettings.orderPaths.find(
        (p) =>
          p.code.toLowerCase() === pathObj.code.toLowerCase() ||
          p.name.toLowerCase() === pathObj.name.toLowerCase()
      );

      if (!existingPath) {
        const cleanName = rawPathName || rawPathCode;
        pathObj = {
          code: rawPathCode || `custom_${Date.now()}_${r}`,
          name: cleanName,
          isSystem: false,
        };
        newSettings.orderPaths.push(pathObj);
        addedPaths++;
      } else {
        pathObj = existingPath;
      }

      for (let c = methodStartCol; c < row.length; c++) {
        const methodObj = colMethods[c];
        if (!methodObj) continue;

        const rawRateStr = row[c];
        if (
          rawRateStr === undefined ||
          rawRateStr === null ||
          rawRateStr === ""
        ) {
          continue;
        }

        const rateVal = parseFloat(
          rawRateStr.replace("%", "").replace(",", ".").trim()
        );
        if (isNaN(rateVal)) continue;

        const ruleIdx = newSettings.rules.findIndex(
          (rule) =>
            (rule.pathCodeOrName.toLowerCase() === pathObj.code.toLowerCase() ||
              rule.pathCodeOrName.toLowerCase() ===
                pathObj.name.toLowerCase()) &&
            (rule.methodCodeOrName.toLowerCase() ===
              methodObj.code.toLowerCase() ||
              rule.methodCodeOrName.toLowerCase() ===
                methodObj.name.toLowerCase())
        );

        if (ruleIdx !== -1) {
          newSettings.rules[ruleIdx].rate = rateVal;
        } else {
          newSettings.rules.push({
            pathCodeOrName: pathObj.code,
            methodCodeOrName: methodObj.code,
            rate: rateVal,
          });
        }
        updatedCount++;
      }
    }
  }

  return {
    settings: newSettings,
    updatedCount,
    addedPaths,
    addedMethods,
  };
}
